/** A print block (design §7.5) — the transaction `report` block verbatim plus
    how it is delivered. Shared by the `print` step and the `print` tool. */

import { useEffect, useState } from 'react'
import { getPrinters, type ProjectPrinter } from '@/api/printers'
import { useWizardDesign } from './context'
import { KeyValueEditor, ObjectPicker, useParamNames } from './pickers'
import type { WizardReportBlock } from './types'
import type { WizardFileBlock, WizardPrintDelivery } from '@twasta/page-runtime/wizard/contract'
import { Field, Grid2, NumberInput, SelectInput, TextInput, hintStyle } from './ui'

export interface PrintValue {
  report?: WizardReportBlock
  delivery?: WizardPrintDelivery
  copies?: number
  printer?: string
}

/** The application's printers (Advanced → Printers), for the `printer` key.
 *  Null while loading or when the list cannot be read (the free-text box
 *  stays usable either way — a printer may be added after the wizard). */
function useProjectPrinters(enabled: boolean): ProjectPrinter[] | null {
  const { project } = useWizardDesign()
  const [printers, setPrinters] = useState<ProjectPrinter[] | null>(null)
  useEffect(() => {
    if (!enabled || !project) return
    let live = true
    getPrinters(project)
      .then((r) => { if (live) setPrinters(r.printers) })
      .catch(() => { if (live) setPrinters(null) })
    return () => { live = false }
  }, [enabled, project])
  return printers
}

export function PrintFields({ value, onChange, formNo, files = false }: {
  value: PrintValue
  onChange: (v: PrintValue) => void
  formNo?: string
  /** The step files its report (a `file` block): `none` delivery is offered. */
  files?: boolean
}) {
  const report = value.report || { obj_name: '' }
  const prompts = useParamNames('R', report.obj_name)
  const patchReport = (p: Partial<WizardReportBlock>) => onChange({ ...value, report: { ...report, ...p } })
  const printers = useProjectPrinters(value.delivery === 'device')
  const defaultPrinter = printers?.find((p) => p.is_default)
  return (
    <div>
      <Grid2>
        <Field label="Report">
          <ObjectPicker objType="R" value={report.obj_name} onChange={(obj_name) => patchReport({ obj_name })} />
        </Field>
        <Field label="Delivery">
          <SelectInput value={value.delivery || 'print_dialog'}
            onChange={(d) => onChange({ ...value, delivery: d as WizardPrintDelivery })}
            options={[
              { value: 'print_dialog', label: 'Print dialog (the browser’s printers)' },
              { value: 'preview', label: 'Preview in the report viewer' },
              { value: 'download', label: 'Download' },
              { value: 'device', label: 'A named label / receipt printer' },
              ...(files ? [{ value: 'none', label: 'Don’t print — only file it' }] : []),
            ]} />
        </Field>
        <Field label="Output format">
          <SelectInput value={report.output_format || ''} empty="Report default"
            onChange={(f) => patchReport({ output_format: f || undefined })}
            options={['pdf', 'html', 'xlsx', 'csv', 'docx']} />
        </Field>
        <Field label="Copies">
          <NumberInput value={value.copies} min={1} onChange={(copies) => onChange({ ...value, copies })} />
        </Field>
        {value.delivery === 'device' && (
          <Field label="Printer">
            {printers && printers.length > 0 ? (
              <SelectInput value={value.printer || ''}
                empty={defaultPrinter ? `Default (${defaultPrinter.name})` : 'Default printer'}
                onChange={(printer) => onChange({ ...value, printer: printer || undefined })}
                options={printers.map((p) => ({
                  value: p.name,
                  label: `${p.name} — ${p.kind.toUpperCase()}${p.enabled ? '' : ' (switched off)'}`,
                }))} />
            ) : (
              <TextInput value={value.printer} onChange={(printer) => onChange({ ...value, printer: printer || undefined })}
                placeholder="dock-1-labels" />
            )}
          </Field>
        )}
      </Grid2>
      {value.delivery === 'device' && (
        <div style={hintStyle}>
          Printers and their site print agent are set up in Advanced → Printers; until one is, the user is told so.
          A ZPL / ESC/POS printer prints the report&apos;s label template (REPORT.LABEL_TEMPLATE), not the report.
        </div>
      )}
      <Field label="Prompt values (by prompt name)">
        <KeyValueEditor value={report.param_map} onChange={(param_map) => patchReport({ param_map })}
          keyOptions={prompts || undefined} formNo={formNo} keyPlaceholder="PROMPT" />
        <div style={hintStyle}>A report prints SAVED data — print this record only after a save step.</div>
      </Field>
    </div>
  )
}

/** A print step's `file` block: the rendered report is FILED into a document
 *  repository (K) for the saved record — the repository copy is the record
 *  (its type's retention, versions and signatures apply). Every value is read
 *  from the model on the server; templates are filled from the STORED record. */
export function FileFields({ value, onChange, formNo }: {
  value: WizardFileBlock | undefined
  onChange: (v: WizardFileBlock | undefined) => void
  formNo?: string
}) {
  const ctx = useWizardDesign()
  const on = !!value
  const block: WizardFileBlock = value || { repository: '' }
  const repo = ctx.catalog?.repositories.find((r) => r.obj_name === block.repository)
  const patch = (p: Partial<WizardFileBlock>) => onChange({ ...block, ...p })
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '10px 0 6px' }}>
        <input type="checkbox" checked={on} disabled={ctx.readOnly}
          onChange={(e) => onChange(e.target.checked ? { repository: '', as: 'filed' } : undefined)} />
        File the report in a document repository
      </label>
      {on && (
        <>
          <Grid2>
            <Field label="Repository">
              <ObjectPicker objType="K" value={block.repository}
                onChange={(repository) => patch({ repository, doctype: undefined })} />
            </Field>
            <Field label="Document type">
              {repo && repo.doctypes.length > 0 ? (
                <SelectInput value={block.doctype || ''} empty="— none —"
                  onChange={(doctype) => patch({ doctype: doctype || undefined })}
                  options={repo.doctypes.map((t) => ({ value: t.key, label: t.title ? `${t.key} — ${t.title}` : t.key }))} />
              ) : (
                <TextInput value={block.doctype} placeholder="TYPE_KEY"
                  onChange={(doctype) => patch({ doctype: doctype || undefined })} />
              )}
            </Field>
            <Field label="Folder">
              <TextInput value={block.folder} placeholder="Quality/Certificates"
                onChange={(folder) => patch({ folder: folder || undefined })} />
            </Field>
            <Field label="Kept in variable">
              <TextInput value={block.as} placeholder="filed"
                onChange={(as) => patch({ as: as || undefined })} />
            </Field>
            <Field label="Title">
              <TextInput value={block.title} placeholder="Certificate {header.batch_no}"
                onChange={(title) => patch({ title: title || undefined })} />
            </Field>
            <Field label="File name">
              <TextInput value={block.filename} placeholder="CERT_{header.batch_no}"
                onChange={(filename) => patch({ filename: filename || undefined })} />
            </Field>
          </Grid2>
          <Field label="Index fields (by field key)">
            <KeyValueEditor value={block.metadata} formNo={formNo} keyPlaceholder="FIELD_KEY"
              onChange={(metadata) => patch({ metadata: Object.keys(metadata).length ? metadata : undefined })} />
          </Field>
          <div style={hintStyle}>
            Filed as the caller (they need Add on the repository; filing again for the same record adds a
            version). A sign step can then sign it, and a send can attach this filed copy.
          </div>
        </>
      )}
    </div>
  )
}
